import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useArchitectureStore } from '../store/useArchitectureStore';
import { REGIONAL_IDENTITIES } from '../data/regions';

export type ArchitecturalStyle = 'Traditional' | 'Transitional' | 'Contemporary';

export interface RegionalIdentity {
    id: string;
    name: string;
    description: string;
    guideline_data: {
        voice_instruction: string; // [NEW] The "Non-negotiables"
        forbidden_elements: string[]; // [NEW] For Negative Prompt
        default_style: ArchitecturalStyle;
        styles: Record<ArchitecturalStyle, {
            massing: string;
            roof_type: string;
            window_ratio: string;
            materials: string[];
            details: string[]; // [NEW] Specific architectural details
            color_proportions?: {
                primary: string;
                accent: string;
            };
        }>;
        palette_instruction: string; // [NEW] Specific RAL/Color instruction
        pattern_images?: string[]; // [NEW] For pattern overlay
        palette?: { // Keeping for backward compatibility if needed, but palette_instruction is primary
            primary: string[];
            secondary: string[];
            accents: string[];
        };
        materials?: { // Keeping for backward compatibility
            walls: string[];
            base: string[];
            openings: string[];
        };
        elements: {
            name: string;
            type: string;
            description: string;
            prompt_terms: string[];
        }[];
    };
}

export const useRegionalGuidelines = () => {
    const { selectedRegionId } = useArchitectureStore();
    const [regions, setRegions] = useState<RegionalIdentity[]>([]);
    const [activeGuideline, setActiveGuideline] = useState<RegionalIdentity | null>(null);
    const [loading, setLoading] = useState(true);

    // Fetch from Supabase
    useEffect(() => {
        const fetchRegions = async () => {
            setLoading(true);
            try {
                const { data, error } = await supabase
                    .from('regional_identities')
                    .select('*');

                if (error) throw error;

                if (data) {
                    // Transform data if necessary, though it should match if JSON is correct
                    setRegions(data as RegionalIdentity[]);
                }
            } catch (error) {
                console.error("Error fetching regions:", error);
                // Fallback to mock if DB fails (Safety net)
                setRegions(REGIONAL_IDENTITIES as any);
            } finally {
                setLoading(false);
            }
        };

        fetchRegions();
    }, []);

    // Set active guideline when selection changes
    useEffect(() => {
        if (selectedRegionId && regions.length > 0) {
            const guideline = regions.find(r => r.id === selectedRegionId);
            setActiveGuideline(guideline || null);
        } else {
            setActiveGuideline(null);
        }
    }, [selectedRegionId, regions]);

    return {
        regions,
        activeGuideline,
        loading
    };
};
